/*
 * SETTINGS (direction §12).
 *
 * "Account (email; Tracked fencers entry with 'N of 10'); Appearance (theme
 * System/Light/Dark segmented control, Reduce motion toggle); Active sessions
 * (device, approximate location, last seen; Revoke per remote session — the
 * current device shows no Revoke, only Sign out); Sign out. No language selector
 * until a second language ships."
 *
 * THE ORDER IS BY PREDICTED USE, NOT §12'S ORDER: Fencers, Notifications, Email
 * summaries, Appearance, Account, Active sessions (decision 326, amended by
 * 622 for the summaries section). §12 lists Account first, which is the
 * conventional shape and is wrong for this product — Account holds the email,
 * its verification state and the change-email form (decision 608), which are
 * rarely visited, while Fencers is the list a coach actually opens Settings
 * to edit. Sorting by what a section is FOR beats sorting by what is
 * administratively senior. The sections themselves and their contents are
 * unchanged; only their sequence differs.
 *
 * THE SESSIONS TABLE IS THE POINT OF THIS SCREEN, and §2 says why: it "doubles as
 * 'track active locations'". It is the only place a user can see that something
 * else is signed in as them, so its honesty matters more than its polish.
 *
 * TWO THINGS IT DOES NOT SHOW, both deliberate:
 *
 *   LOCATION. §12 asks for "approximate location" and there is no source for it.
 *   `user_sessions` stores the IP (stored, never displayed, per decision 307) and
 *   there is no geo-IP provider — `internal/geo` is country-code normalisation for
 *   the identity scorer, not IP lookup. Rather than a fabricated city, the row
 *   shows device and last-seen, which is what the security question actually turns
 *   on (deviations.md #3).
 *
 *   A REVOKE BUTTON ON THE CURRENT DEVICE. §12 is explicit. Revoking your own
 *   session is signing out, and offering both spellings of one action in one list
 *   invites someone to "revoke" what they are using and wonder why they were
 *   logged out.
 *
 * THERE IS A LANGUAGE SELECTOR NOW, and §12's "no language selector until a
 * second language ships" is superseded (decision 539). The old rule was
 * principle 9's — a picker with one option is chrome for a plurality that does
 * not exist — and what changed is not the principle but the fact it rested on:
 * a catalogue is now a file, `GET /api/locales` enumerates whatever files the
 * build holds, and Kevin adds the other languages before deploy, so the
 * single-option case this screen was protecting against will not exist in
 * production. Gating the row on `locales.length > 1` was considered and
 * declined with the same reasoning: it would hide the control on exactly the
 * development build where somebody needs to check it works.
 *
 * The design direction document is a SOURCE and is not rewritten; the decision
 * log records the reversal.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Ago } from "../components/Ago";
import { AddressFlag, EmailAddress, ResendVerificationButton } from "../components/EmailAddress";
import { SummaryChoice, needsCategory, type SummaryDraft } from "../components/SummaryChoice";
import { ApiError, api, list, type EmailSummary, type LocaleRow, type SessionRow, type User } from "../lib/api";
import { sessionLabel } from "../lib/device";
import { disablePush } from "../lib/push";
import { createPasskey, passkeysSupported, wasCancelled } from "../lib/webauthn";
import styles from "./Settings.module.css";

export interface SettingsProps {
  user: User;
  onUserChanged: (user: User) => void;
  onSignOut: () => void;
  /**
   * The build this server was compiled from (decision 361), rendered as the
   * screen's quiet last line so a defect report can name the build. "dev" and
   * "unknown" are what a workstation build honestly says.
   */
  serverVersion?: string;
  serverCommit?: string;
  /**
   * After the account is deleted (decision 891): the session went with it, so
   * the shell forgets the user and goes home — a sign-out with nothing left
   * on the server to revoke.
   */
  onAccountDeleted: () => void;
}

export function Settings({
  user,
  onUserChanged,
  onSignOut,
  onAccountDeleted,
  serverVersion,
  serverCommit,
}: SettingsProps) {
  // `i18n` for the RESOLVED language, which is what the language row needs and
  // the user row cannot supply: a null `ui_locale` means "follow the device"
  // and only i18next knows what that resolved to.
  const { t, i18n } = useTranslation();

  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [passkeyCount, setPasskeyCount] = useState<number | null>(null);
  const [rosterCount, setRosterCount] = useState<number | null>(null);
  // Set with the roster count, from the primary list's `cap` (decision 889).
  const [cap, setCap] = useState<number | null>(null);
  const [listCount, setListCount] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);

  /*
   * The available catalogues (decision 537). Seeded with English rather than
   * empty, because English is BUNDLED: it is available before any request and
   * stays available if the request never lands, so an empty initial state would
   * render a picker that briefly offered nothing.
   */
  const [locales, setLocales] = useState<LocaleRow[]>([
    { code: "en", name: "English", flag: "🇺🇸" },
  ]);

  // The change-password form. Collapsed until asked for, so the common case (a
  // coach who never changes it) sees one link rather than two always-open
  // credential fields. Its own notice, kept apart from the passkey one so a
  // success on one does not read as a result of the other.
  const [showPassword, setShowPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // Never sent anywhere: the wire keeps one password field (decision 367).
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMismatch, setPasswordMismatch] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordDone, setPasswordDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void api
      .sessions()
      .then((b) => !cancelled && setSessions(list(b.sessions)))
      .catch(() => undefined);

    void api
      .passkeys()
      .then((b) => !cancelled && setPasskeyCount(list(b.passkeys).length))
      .catch(() => undefined);

    // The roster count for §12's "N of 10". Read from the server rather than
    // counted client-side, and the cap comes with it, so the arithmetic has one
    // source (see ListBody.Cap).
    //
    // ACROSS MULTIPLE LISTS THE COUNT IS THE PLAIN SUM (decision 531), not
    // distinct fencers — Kevin: "just sum the list counts; it does not need
    // to be unique fencers". The cap is only meaningful for one list, so it
    // goes unmentioned once there is more than one.
    void api
      .lists()
      .then((b) => {
        if (cancelled) return;
        const all = list(b.lists);
        const primary = all.find((l) => l.is_primary) ?? all[0];
        if (!primary) return;
        setCap(primary.cap);
        setListCount(all.length);
        setRosterCount(all.reduce((sum, l) => sum + l.member_count, 0));
      })
      .catch(() => undefined);

    // The languages this build can render in (decision 537). Read alongside the
    // other three rather than in a second effect, and a FAILURE LEAVES THE ROW
    // SHOWING ENGLISH ALONE rather than an error: English is statically bundled,
    // so it is true whatever the network did, and a language picker is not worth
    // an error banner on a settings page.
    void api
      .locales()
      .then((b) => !cancelled && setLocales(list(b.locales)))
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  async function savePreferences(
    patch: Partial<{
      theme: User["theme"];
      reduce_motion: boolean;
      /**
       * `null` is a VALUE here and not "leave it alone": it is the stored form
       * of "follow the device" (decision 537), so the language row writes it
       * deliberately. `undefined` is what means untouched, which is why the
       * coalesce below is `??` on a field that can legitimately be null.
       */
      ui_locale: string | null;
    }>,
  ) {
    // The WHOLE preference object is sent, because the endpoint is a PUT: sending
    // a partial would reset the fields it omitted. The client's copy of the user
    // is the source for the untouched ones.
    const uiLocale = patch.ui_locale === undefined ? (user.ui_locale ?? null) : patch.ui_locale;
    const body = await api.preferences({
      theme: patch.theme ?? user.theme,
      reduce_motion: patch.reduce_motion ?? user.reduce_motion,
      upcoming_group_by: user.upcoming_group_by,
      past_group_by: user.past_group_by,
      // Round-trips unchanged: this screen has no control that sets it, and
      // omitting it from a whole-object PUT would reset it (decision 535).
      notification_name_format: user.notification_name_format,
      ...(uiLocale === null ? {} : { ui_locale: uiLocale }),
      // The persona hint round-trips UNCHANGED. It is typed as a closed union on
      // the way in and a plain string on the way out, so the cast is narrowing a
      // value the server itself produced — never a value from this screen, which
      // has no control that sets it.
      ...(user.persona_hint
        ? { persona_hint: user.persona_hint as "fencer" | "parent" | "coach" }
        : {}),
    });
    onUserChanged(body.user as User);
  }

  async function revoke(id: string) {
    await api.revokeSession(id);
    const b = await api.sessions();
    setSessions(list(b.sessions));
  }

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setPasswordError(null);
    setPasswordDone(false);
    setPasswordMismatch(false);

    /*
     * REFUSED BEFORE ANY REQUEST (roadmap stage 2 item 5). The stakes are higher
     * here than at registration: a successful change revokes every session for
     * the account including this one (decision 367), so committing a mistyped
     * password signs the user out of everything and into nothing — recoverable
     * since decision 607 only by an email round trip through the reset link.
     *
     * Display and flow only. `changePassword` still takes one new password; the
     * confirmation's job is finished the moment the two agree.
     */
    if (newPassword !== confirmPassword) {
      setPasswordMismatch(true);
      return;
    }

    setPasswordBusy(true);
    try {
      // The server rotates the session on success: it revokes every session,
      // including this one, and the Set-Cookie on the response installs the
      // replacement (decision 367). So the fields clear, and the sessions list is
      // re-read because the other devices are now gone.
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setPasswordDone(true);
      const b = await api.sessions();
      setSessions(list(b.sessions));
    } catch (err) {
      // A wrong current password comes back 400 with a server-kind failure; the
      // handler uses 400 rather than 403 precisely because the client reserves
      // 403 for the pending-account gate.
      const wrongCurrent =
        err instanceof ApiError &&
        err.failure.kind === "server" &&
        err.failure.status === 400;
      setPasswordError(wrongCurrent ? t("settings.passwordWrongCurrent") : t("settings.passwordFailed"));
    } finally {
      setPasswordBusy(false);
    }
  }

  async function addPasskey() {
    setNotice(null);
    try {
      const begun = await api.beginPasskeyRegistration();
      const credential = await createPasskey(begun.options);
      await api.finishPasskeyRegistration(begun.ceremony_id, credential);
      const b = await api.passkeys();
      setPasskeyCount(list(b.passkeys).length);
      setNotice(t("settings.passkeyAdded"));
    } catch (err) {
      if (!wasCancelled(err)) setNotice(t("settings.passkeyFailed"));
    }
  }

  return (
    <div className={styles.screen}>
      <h1 className={styles.h1}>{t("nav.settings")}</h1>

      {/*
        --- Fencers ---

        ITS OWN SECTION, AND AN ACTUAL BUTTON (decision 325). This was a row in
        Account whose only affordance was a red VALUE on the right — and when the
        count failed to load the value was empty, so the row rendered as a plain
        label with nothing red anywhere and no way to tell it was a link at all.
        That is the failure direction §3 names: a thing you can click has to look
        like a thing you can click, and an affordance carried entirely by data is
        one that disappears exactly when something has gone wrong.

        So the count is now a statement and the action is a control. The button is
        present whether or not the count arrives, which is the property the old row
        did not have — and the count is simply absent when unknown rather than
        guessing at zero, since "no fencers" and "we could not ask" are different
        facts (contract §0).

        "FENCERS", NOT "MY FENCERS" and not "TRACKED". §2 requires neutral wording
        across personas and rules out possessives by name: the same words serve a
        coach, a parent, and a fencer tracking themselves — and "my fencers" is
        wrong for the third. `manage.test.tsx` enforces this. "Tracked" was the
        word being replaced. The bare noun carries it, and the count line supplies
        the arithmetic §12 asks for without a verb at all.

        Likely to be promoted to its own nav item later; a section here is the
        step that does not pretend otherwise.
      */}
      <section className={styles.section}>
        <h2 className={styles.h2}>{t("settings.fencers")}</h2>

        {rosterCount === null || cap === null ? null : (
          <p className={styles.sectionNote}>
            {listCount > 1
              ? t("settings.fencerCountLists", { count: rosterCount, lists: listCount })
              : t("settings.fencerCount", { count: rosterCount, cap })}
          </p>
        )}

        <Link className={styles.manageButton} to="/settings/fencers">
          {t("settings.manageFencers")}
        </Link>
      </section>

      {/* --- Notifications ---
        A ROW THAT NAVIGATES, not a sixth inline section (decision 395). The
        per-list matrix of kinds by channels is a page's worth of controls, and
        it sits beside Manage fencers because they are the same shape of thing:
        a settings entry whose content is a screen.

        Placed directly after Fencers, which is decision 380's ordering rule —
        sections run by predicted use, and "who am I tracking" and "what does it
        tell me about them" are the same question asked twice.
      */}
      <section className={styles.section}>
        <h2 className={styles.h2}>{t("settings.notifications")}</h2>
        <p className={styles.sectionNote}>{t("settings.notificationsNote")}</p>
        <Link className={styles.manageButton} to="/settings/notifications">
          {t("settings.manageNotifications")}
        </Link>
      </section>

      {/* --- Email summaries ---
        AN INLINE SECTION, NOT A PAGE (decision 622): five controls, where
        decision 395's own page exists for the per-list tree. Between
        Notifications and Appearance, because it answers the same question as the
        section above it -- what does this tell me -- for the other channel.
      */}
      <EmailSummaries user={user} />

      {/* --- Appearance --- */}
      <section className={styles.section}>
        <h2 className={styles.h2}>{t("settings.appearance")}</h2>

        <div className={styles.row}>
          <span className={styles.rowLabel} id="theme-label">
            {t("settings.themeLabel")}
          </span>
          {/*
            A three-way segmented control, so it is a radiogroup rather than three
            buttons — one choice, and a screen reader should hear it as one.
          */}
          <div className={styles.segmented} role="radiogroup" aria-labelledby="theme-label">
            {(["system", "light", "dark"] as const).map((choice) => {
              const selected = user.theme === choice;
              return (
                <button
                  key={choice}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  /*
                    THE COLOUR RULE, same as the grouping toggle: the UNSELECTED
                    segments are red, because pressing the one you are on does
                    nothing. Red means touchable (principle 3).
                  */
                  className={selected ? styles.segSelected : styles.segUnselected}
                  onClick={() => void savePreferences({ theme: choice })}
                >
                  {t(`settings.theme.${choice}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.row}>
          <label className={styles.rowLabel} htmlFor="reduce-motion">
            {t("settings.reduceMotion")}
          </label>
          {/*
            A real checkbox. §6 requires the in-app toggle to be honoured
            ALONGSIDE prefers-reduced-motion, not instead of it — so this can only
            ever add suppression, never re-enable motion for someone whose OS says
            no.
          */}
          <input
            id="reduce-motion"
            className={styles.checkbox}
            type="checkbox"
            checked={user.reduce_motion}
            onChange={(e) => void savePreferences({ reduce_motion: e.target.checked })}
          />
        </div>

        {/* --- Language (decision 537) ---
          INSIDE APPEARANCE, and the section order does not change: decision 326
          orders Settings by predicted use and language is a property of how the
          app LOOKS to you, beside the theme and the motion toggle.

          TWO CONTROLS AND NOT ONE. A single `<select>` with "System default" as
          its first option would make following the device look like a choice of
          language, which it is not — it is a choice not to choose one, and it
          keeps following a device whose setting later changes. The checkbox says
          that, and the `<select>` beside it is disabled while it is checked
          because a control with no outcome is what decision 418 rules out.

          BOTH RENDER EVEN WITH ONE CATALOGUE, reversing this file's own
          principle-9 argument (Kevin: "I will add additional files before
          deploy, so a single-file case will never exist in production").
        */}
        <div className={styles.row}>
          <span className={styles.rowLabel} id="language-label">
            {t("settings.language")}
          </span>
          <div className={styles.rowValue}>
            <label className={styles.inlineLabel} htmlFor="language-system">
              <input
                id="language-system"
                className={styles.checkbox}
                type="checkbox"
                checked={user.ui_locale == null}
                onChange={(e) =>
                  /*
                    UNCHECKING WRITES THE CURRENTLY RESOLVED LOCALE, not a blank
                    and not English: the reader is turning "follow the device"
                    into "keep this one", and the one thing that must not happen
                    is the language changing under them at the moment they said
                    to pin it.
                  */
                  void savePreferences({
                    ui_locale: e.target.checked ? null : (i18n.resolvedLanguage ?? "en"),
                  })
                }
              />
              {t("settings.languageSystem")}
            </label>
            <select
              className={styles.select}
              aria-labelledby="language-label"
              value={user.ui_locale ?? (i18n.resolvedLanguage ?? "en")}
              disabled={user.ui_locale == null}
              onChange={(e) => void savePreferences({ ui_locale: e.target.value })}
            >
              {locales.map((l) => (
                <option key={l.code} value={l.code}>
                  {/* THE FLAG BEFORE THE NAME, and nothing at all when the
                      catalogue declares none (weekend enhancements, item 4).
                      A native <select>'s options take text only, so this is
                      string concatenation rather than markup. */}
                  {l.flag ? `${l.flag} ${l.name}` : l.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* --- Account --- */}
      <section className={styles.section}>
        <h2 className={styles.h2}>{t("settings.account")}</h2>

        <div className={styles.row}>
          <span className={styles.rowLabel}>{t("auth.email")}</span>
          <span className={styles.rowValue}>
            {user.email}
            <AddressFlag user={user} showVerified />
          </span>
        </div>
        {/*
          The address's state, another verification email, and change of
          address (decisions 605, 608). Shared with the pending screen, which
          needs the same three things for the same reasons.
        */}
        <EmailAddress user={user} variant="settings" onUserChanged={onUserChanged} />

        {/*
          Passkeys. Not in §12's list, and included because §2 requires all three
          credential types to WORK — and a passkey cannot be added at sign-in,
          since registering one needs an account to attach it to. Without an entry
          here, a user who signed up with a password could never get one.
        */}
        {passkeysSupported() ? (
          <div className={styles.row}>
            <span className={styles.rowLabel}>{t("settings.passkeys")}</span>
            <span className={styles.rowValue}>
              {passkeyCount === null
                ? null
                : t("settings.passkeyCount", { count: passkeyCount })}
              <button className={styles.inlineAction} type="button" onClick={() => void addPasskey()}>
                {t("settings.addPasskey")}
              </button>
            </span>
          </div>
        ) : null}

        {/*
          Change password. A real <form> with the standard autocomplete tokens,
          for the same password-manager reason SignIn is built the way it is
          (direction §2): current-password to authorise, new-password so a manager
          offers to generate and then save the replacement. No paste-blocking,
          both fields present at once. A forgotten password is reset from the
          sign-in screen's link rather than from here (decision 607): someone
          signed in here can change it with the current one.
        */}
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t("settings.password")}</span>
          <span className={styles.rowValue}>
            <button
              className={styles.inlineAction}
              type="button"
              onClick={() => {
                setShowPassword((v) => !v);
                setPasswordError(null);
                setPasswordDone(false);
              }}
            >
              {t("settings.changePassword")}
            </button>
          </span>
        </div>

        {showPassword ? (
          <form className={styles.passwordForm} onSubmit={(e) => void submitPassword(e)}>
            {/* A hidden username field so a password manager attaches the saved
                credential to this account rather than guessing (a bare pair of
                password fields with no username confuses several managers). */}
            <input
              type="text"
              name="username"
              autoComplete="username"
              value={user.email}
              readOnly
              hidden
            />
            <label className={styles.passwordLabel} htmlFor="current-password">
              {t("settings.currentPassword")}
            </label>
            <input
              id="current-password"
              className={styles.passwordInput}
              type="password"
              name="current-password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <label className={styles.passwordLabel} htmlFor="new-password">
              {t("settings.newPassword")}
            </label>
            <input
              id="new-password"
              className={styles.passwordInput}
              type="password"
              name="new-password"
              autoComplete="new-password"
              // Twelve, matching register's form and all three server-side
              // floors (decision 386). This said 10 while register said 12,
              // which made "the same floor register uses" false on the one
              // screen that repeated the claim.
              minLength={12}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            {/*
              CONFIRM (roadmap stage 2 item 5), carrying `new-password` as well —
              the token is what makes a manager fill BOTH halves of a
              generate-and-save pair rather than only the first, so leaving it
              off would make the password manager the reason the form fails.
            */}
            <label className={styles.passwordLabel} htmlFor="confirm-password">
              {t("settings.confirmPassword")}
            </label>
            <input
              id="confirm-password"
              className={styles.passwordInput}
              type="password"
              name="confirm-password"
              autoComplete="new-password"
              {...(passwordMismatch ? { "aria-describedby": "confirm-password-mismatch" } : {})}
              // No minLength, unlike the field above: the floor belongs to the
              // password, and putting it here answers a short mismatch with the
              // browser's own bubble rather than with our sentence (§4).
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordMismatch(false);
              }}
              required
            />
            <p className={styles.passwordHint}>{t("settings.passwordHint")}</p>
            {/*
              A PLAIN SENTENCE, without role="alert" — unlike the server-failure
              notice below it, which IS news arriving from elsewhere. A mismatch
              is something the reader can see in the field they just left, and an
              alert role interrupts a screen reader to announce it to them.
            */}
            {passwordMismatch ? (
              <p id="confirm-password-mismatch" className={styles.notice}>
                {t("settings.passwordMismatch")}
              </p>
            ) : null}
            {passwordError ? (
              <p className={styles.notice} role="alert">
                {passwordError}
              </p>
            ) : null}
            <button className={styles.manageButton} type="submit" disabled={passwordBusy}>
              {t("settings.savePassword")}
            </button>
          </form>
        ) : null}

        {passwordDone ? (
          <p className={styles.notice} role="status">
            {t("settings.passwordChanged")}
          </p>
        ) : null}

        {notice ? (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        ) : null}
      </section>

      {/* --- Active sessions --- */}
      <section className={styles.section}>
        <h2 className={styles.h2}>{t("settings.sessions")}</h2>
        <p className={styles.sectionNote}>{t("settings.sessionsNote")}</p>

        <ul className={styles.sessionList}>
          {sessions.map((s) => (
            <li key={s.id} className={styles.session}>
              <div className={styles.sessionMain}>
                <span className={styles.rowValue}>
                  {/*
                    An unparsed User-Agent says so rather than printing the raw
                    string: a wall of "Mozilla/5.0 (Macintosh; Intel Mac OS X
                    10_15_7)" is not something a user can act on, and the
                    last-seen time below still tells their devices apart.
                  */}
                  {/* A native app's session by its device's own name; any
                      other from its User-Agent (lib/device.ts). */}
                  {sessionLabel(s, t)}
                  {s.is_current ? (
                    // The current device, named rather than badged: "presence
                    // implies state" does not apply here, because both rows are
                    // present and only one is you.
                    <span className={styles.thisDevice}> · {t("settings.thisDevice")}</span>
                  ) : null}
                </span>
                <span className={styles.sessionMeta}>
                  {/*
                    A BARE RELATIVE TIME WITH ITS OWN LABEL, not AsOf, and this is
                    decision 322's rule applied where it had been missed
                    (decision 327). AsOf phrases itself as a sentence about an
                    OBSERVATION — reading a source — and a session's `last_seen_at`
                    is not that: it is when this device last made a request, which
                    is something a PERSON did. The distinction was invisible while
                    both said "Checked N ago" and became obvious the moment the
                    freshness footer's copy grew more specific.
                  */}
                  {t("settings.lastActive")} <Ago at={s.last_seen_at} />
                </span>
              </div>

              {/* No Revoke on the current device (§12) — Sign out below is that. */}
              {s.is_current ? null : (
                <button
                  className={styles.revoke}
                  type="button"
                  onClick={() => void revoke(s.id)}
                >
                  {t("settings.revoke")}
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section}>
        <button className={styles.signOut} type="button" onClick={onSignOut}>
          {t("settings.signOut")}
        </button>
      </section>

      {/* --- Delete account (decision 891) ---
        LAST, below Sign out: the one action on this screen that cannot be
        undone sits where nobody reaches it on the way to something else. */}
      <DeleteAccount user={user} onDeleted={onAccountDeleted} />

      {/*
        THE BUILD FOOTER (decision 361): the deploy tag and commit, in the
        smallest type on the screen, so "which build did this happen on" has an
        answer a user can read aloud. Absent entirely when the client never
        learned them — a missing version must not render as an empty sentence.
      */}
      {serverVersion ? (
        <p className={styles.buildFooter}>
          {t("settings.build", {
            version: serverVersion,
            commit: (serverCommit ?? "unknown").slice(0, 7),
          })}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The email summaries section (decisions 613, 622). At module scope rather
 * than inside Settings, decision 492's lesson: a component declared in a
 * render function is a new type every render, and its checkboxes remount and
 * lose focus mid-interaction.
 *
 * SAVES THE WAY APPEARANCE DOES, on every change and with no Save button --
 * except that "on with nothing ticked" is held locally with a sentence beneath
 * and NOT sent, because the server would refuse it and a control that flashed
 * an error for a half-finished choice would be scolding the reader for an
 * intermediate step.
 *
 * THE ADDRESS'S STATE IS SPOKEN HERE as well as in Account, because it decides
 * whether this section does anything: an unverified address is never mailed,
 * and a bouncing one is paused (decision 620). Only while summaries are on --
 * off, there is nothing for the address to block.
 */
function EmailSummaries({ user }: { user: User }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SummaryDraft | null>(null);
  const [failed, setFailed] = useState<"load" | "save" | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .emailSummary()
      .then((b) => !cancelled && setDraft(b))
      .catch(() => !cancelled && setFailed("load"));
    return () => {
      cancelled = true;
    };
  }, []);

  async function change(proposed: SummaryDraft) {
    // TURNING SUMMARIES ON WITH NOTHING CHOSEN PICKS A STARTING POINT (Kevin's
    // review): results and registration changes ticked, deadlines and schedule
    // changes not -- so the switch itself saves, and the reader adjusts from
    // there. Onboarding preselects nothing; this is Settings' answer to a
    // section that was just collapsed.
    const next =
      draft?.frequency === "off" && proposed.frequency !== "off" && needsCategory(proposed)
        ? { ...proposed, results: true, registrations: true }
        : proposed;
    setDraft(next);
    setFailed(null);
    if (needsCategory(next) || next.frequency === null) return;
    try {
      const body: EmailSummary = { ...next, frequency: next.frequency };
      setDraft(await api.setEmailSummary(body));
    } catch {
      setFailed("save");
    }
  }

  const on = draft?.frequency === "daily" || draft?.frequency === "weekly";

  return (
    <section className={styles.section}>
      <h2 className={styles.h2}>{t("emailSummary.heading")}</h2>
      <p className={styles.sectionNote}>{t("emailSummary.note")}</p>

      {draft ? (
        <SummaryChoice value={draft} onChange={(n) => void change(n)} withOff idPrefix="settings-summary" />
      ) : failed === "load" ? (
        <p className={styles.notice}>{t("emailSummary.loadFailed")}</p>
      ) : null}

      {failed === "save" ? (
        <p className={styles.notice} role="alert">
          {t("emailSummary.saveFailed")}
        </p>
      ) : null}

      {on && user.email_status === "unverified" ? (
        <>
          <p className={styles.notice}>{t("emailSummary.unverified", { email: user.email })}</p>
          <ResendVerificationButton user={user} />
        </>
      ) : null}
      {on && user.email_status === "bounced" ? (
        <>
          <p className={styles.bouncedNotice}>{t("emailSummary.bounced")}</p>
          <ResendVerificationButton user={user} />
          <p className={styles.notice}>{t("emailSummary.bouncedChange")}</p>
        </>
      ) : null}
    </section>
  );
}

/**
 * Deleting the account (decision 891), at module scope for EmailSummaries'
 * reason.
 *
 * IN PLACE, NOT A MODAL (509's posture, which 481 drew from 370): a destructive
 * confirmation inside a transient overlay invites dismissal mid-decision. The
 * section shows one bordered button; pressing it opens the confirmation where
 * the button was.
 *
 * THE CONFIRMATION IS THE ACCOUNT'S OWN CREDENTIAL, OR A TYPED WORD. With a
 * password the reader types it, on a real form with `current-password` so a
 * manager can fill it; without one (a passkey or OIDC account) they type the
 * word the prompt shows, in their own language, checked here and sent as
 * `confirm: "delete"`. It used to be their address typed back, which for a
 * Sign in with Apple account is a relay address nobody has seen (Kevin: "too
 * hostile"). The server checks whichever applies — a destructive endpoint
 * does not trust that the confirmation happened.
 */
function DeleteAccount({ user, onDeleted }: { user: User; onDeleted: () => void }) {
  const { t, i18n } = useTranslation();
  const word = t("settings.deleteAccount.word");
  const [confirming, setConfirming] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close() {
    setConfirming(false);
    setValue("");
    setError(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // A mistyped word is the reader's to correct, and asks nothing of the
    // server: compared trimmed and without case, in the prompt's language.
    if (!user.has_password && value.trim().toLocaleLowerCase(i18n.language) !== word.toLocaleLowerCase(i18n.language)) {
      setError(t("settings.deleteAccount.wrongWord", { word }));
      return;
    }
    setBusy(true);
    try {
      await api.deleteMyAccount(
        user.has_password ? { current_password: value } : { confirm: "delete" },
      );
      // The server deleted this browser's push subscription with the account;
      // the browser's own copy goes too, so nothing is left subscribed to an
      // account that no longer exists. Best-effort, and the server half is a
      // no-op because there is no row left to remove.
      void disablePush(async () => undefined).catch(() => undefined);
      onDeleted();
    } catch (err) {
      if (!(err instanceof ApiError)) {
        setError(t("settings.deleteAccount.failed"));
      } else if (err.failure.kind === "server" && err.failure.status === 400) {
        setError(t("settings.deleteAccount.wrongPassword"));
      } else if (err.failure.kind === "server" && err.failure.status === 422) {
        setError(t("settings.deleteAccount.wrongWord", { word }));
      } else if (err.failure.kind === "onboarding-incomplete") {
        // THE LAST-ADMIN 409. `failureFor` turns every 409 into
        // "onboarding-incomplete" (a defect reported with decision 891 and left
        // as it is), and Settings is reachable only past the onboarding gate, so
        // on this call a 409 can only be the server's last-admin refusal.
        setError(t("settings.deleteAccount.lastAdmin"));
      } else {
        setError(t("settings.deleteAccount.failed"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.section}>
      <h2 className={styles.h2}>{t("settings.deleteAccount.heading")}</h2>
      <p className={styles.sectionNote}>{t("settings.deleteAccount.note")}</p>

      {confirming ? (
        <form className={styles.passwordForm} onSubmit={(e) => void submit(e)}>
          {user.has_password ? (
            <>
              {/* The hidden username, for the change-password form's reason: a
                  manager attaches the saved credential to this account. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={user.email}
                readOnly
                hidden
              />
              <label className={styles.passwordLabel} htmlFor="delete-password">
                {t("settings.deleteAccount.passwordPrompt")}
              </label>
              <input
                id="delete-password"
                className={styles.passwordInput}
                type="password"
                name="current-password"
                autoComplete="current-password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
              />
            </>
          ) : (
            <>
              <label className={styles.passwordLabel} htmlFor="delete-word">
                {t("settings.deleteAccount.wordPrompt", { word })}
              </label>
              <input
                id="delete-word"
                className={styles.passwordInput}
                type="text"
                name="confirm-word"
                // Typed, never filled: the point is that a person types it.
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
              />
            </>
          )}
          {error ? (
            <p className={styles.notice} role="alert">
              {error}
            </p>
          ) : null}
          <div className={styles.deleteActions}>
            <button className={styles.greyButton} type="button" disabled={busy} onClick={close}>
              {t("settings.deleteAccount.cancel")}
            </button>
            <button className={styles.dangerButton} type="submit" disabled={busy}>
              {t("settings.deleteAccount.confirm")}
            </button>
          </div>
        </form>
      ) : (
        <button className={styles.dangerButton} type="button" onClick={() => setConfirming(true)}>
          {t("settings.deleteAccount.start")}
        </button>
      )}
    </section>
  );
}
